/**
 * Gate for the /support-console/* route subtree (the support-agent surface).
 *
 * Access requires a support-staff OR platform-staff user. Mirrors
 * PlatformRouteGuard but keyed on `/v1/support/agent/me` so an agent who is
 * NOT a full platform super-admin can still reach the support console.
 *
 * The MFA-enrolment gate (`!me.data.mfa_enabled` → inline notice) was
 * removed 2026-09-08 (backlog §9.3, "don't force 2FA/passkey enrollment
 * anywhere"). The backend no longer enforces this either
 * (`apps.support.permissions.IsSupportStaff`).
 */
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { isAuthenticated } from "@/lib/auth";
import { useAgentMe } from "@/lib/support";
import { Spinner } from "./Spinner";

export function SupportConsoleRouteGuard() {
  const { t } = useTranslation();
  const location = useLocation();
  const me = useAgentMe();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (me.isLoading) return <Spinner fullscreen label={t("support.console.loading")} />;
  if (me.isError || !me.data) return <Navigate to="/dashboard" replace />;

  const isAgent = me.data.is_support_staff || me.data.is_platform_staff;
  if (!isAgent) return <Navigate to="/dashboard" replace />;

  return <Outlet />;
}
